import { Box, CircularProgress, IconButton } from "@mui/material";
import { useState } from "react";
import ByzCard from "src/components/core-components/ByzCard";
import ByzTitleBox from "src/components/core-components/ByzTitleBox";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import NurseApplicationCreate, {
  NurseApplicationFormData,
} from "./NurseApplicationCreate";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";

interface NurseApplicationProps {
  rows: Record<string, any>[];
  handleDeleteClickNurseApplications: (rowId: number) => void;
  handleChange: (field: keyof NurseApplicationFormData, value: any) => void;
  save: (id?: string) => void;
  onRefresh?: () => void;
}

function NurseApplication({
  rows,
  handleDeleteClickNurseApplications,
  handleChange,
  save,
  onRefresh,
}: NurseApplicationProps) {
  const { t } = useTranslation();
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);

  const openDeleteConfirm = (id: number) => {
    setSelectedDeleteId(id);
    setOpenConfirm(true);
  };

  const handleEditClick = (id: number) => {
    setEditItemId(id);
    setOpen(true);
  };

  const [open, setOpen] = useState(false);
  const [editItemId, setEditItemId] = useState<number>(0);

  const columns: GridColDef[] = [
    { field: "ID", headerName: "ID", flex: 1 },
    {
      field: "CTIME",
      headerName: t("pain-assessment-scale.nurse-application.ctime"),
      flex: 1,
    },
    {
      field: "CHOUR",
      headerName: t("pain-assessment-scale.nurse-application.c-hour"),
      flex: 1,
    },
    {
      field: "NON_MEDICAL_APP",
      headerName: t("pain-assessment-scale.nurse-application.non-medical-app"),
      flex: 1,
    },
    {
      field: "ATTEMPTS_MADE",
      headerName: t("pain-assessment-scale.nurse-application.attempts"),
      flex: 1,
    },
    {
      field: "SIDE_EFFECTS",
      headerName: t("pain-assessment-scale.nurse-application.side-effects"),
      flex: 1,
    },
    {
      field: "ACTIVITY_CONTROL",
      headerName: t("pain-assessment-scale.nurse-application.activity-control"),
      flex: 1,
    },
    {
      field: "PAIN_SCORE",
      headerName: t("pain-assessment-scale.nurse-application.score"),
      flex: 1,
    },
    {
      field: "NURSE_EXPLANATION",
      headerName: t(
        "pain-assessment-scale.nurse-application.nurse-explanation"
      ),
      flex: 1,
    },
    {
      field: "CUSER",
      headerName: t("pain-assessment-scale.nurse-application.nurse-name"),
      flex: 1,
    },
    {
      field: "delete",
      headerName: t("card.delete"),
      flex: 1,
      renderCell: (params) => (
        <IconButton
          onClick={() => openDeleteConfirm(params.row.ID)}
          disabled={deleteLoading === params.row.ID}
        >
          {deleteLoading === params.row.ID ? (
            <CircularProgress size={20} color="error" />
          ) : (
            <ByzGrdDeleteButton />
          )}
        </IconButton>
      ),
    },
    {
      field: "edit",
      headerName: t("card.edit"),
      flex: 1,
      renderCell: (params) => (
        <IconButton onClick={() => handleEditClick(params.row.ID)}>
          {deleteLoading === params.row.ID ? (
            <CircularProgress size={20} color="info" />
          ) : (
            <ByzGrdEditButton />
          )}
        </IconButton>
      ),
    },
  ];

  return (
    <ByzCard>
      <ByzTitleBox title={t("pain-assessment-scale.nurse-application.title")} />

      {/* Silme Onay Modali */}
      <ByzDeleteConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onDelete={async () => {
          if (!selectedDeleteId) return false;

          setDeleteLoading(selectedDeleteId.toString());
          await handleDeleteClickNurseApplications(selectedDeleteId);
          setDeleteLoading(null);

          if (onRefresh) onRefresh();

          return true;
        }}
        content={t("delete-confirm.title")}
      />

      <Box p={2}>
        <ByzMuiDataGrid columns={columns} pageSize={10} rows={rows} />
      </Box>

      <OpenComponentDialog
        title={
          editItemId
            ? `${t("pain-assessment-scale.nurse-application.title")} ${t(
                "card.edit"
              )}`
            : t("pain-assessment-scale.nurse-application.title")
        }
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        isMedCard={true}
      >
        <NurseApplicationCreate
          recordId={editItemId.toString()}
          handleChange={handleChange}
          onClose={() => {
            setOpen(false);
            if (onRefresh) onRefresh();
          }}
          save={save}
        />
      </OpenComponentDialog>
    </ByzCard>
  );
}

export default NurseApplication;
